import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
} from "@mui/material";
import ActionButtons from "../../../Components/Quetionary/InitialKickoff/MaterialTopics/ActionButtons";
import { useNavigate, useParams } from "react-router-dom";
import FileUpload from "./FileUpload";
import { api, get, put} from "../../../utils/api";
import { numberError, textError } from "../../../utils/validationMessage";
import MainDashboardLayout from "../../../Layout/MainDashboardLayout";
import GovernanceLayout from "../../../Layout/GovernanceLayout";
import {
  formatNumberInputWithCommas,
  stripNumberFormatting,
} from "../../../utils/common";

const TransparancyDisclosure = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [moduleType, setModuleType] = useState("");
  // State variables for form data
  const [financialReportingFrequency, setFinancialReportingFrequency] =
    useState("");
  const [shareholderRights, setShareholderRights] = useState("yes");
  const [esgReportingFrequency, setEsgReportingFrequency] = useState("");
  const [adheresToReportingStandardsInfo, setAdheresToReportingStandardsInfo] =
    useState("");
  const [adheresToReportingStandards, setAdheresToReportingStandards] =
    useState("yes");
  const [externalAuditsConducted, setExternalAuditsConducted] = useState("");
  const [
    externalAuditsConductedDocumentUrl,
    setExternalAuditsConductedDocumentUrl,
  ] = useState("");
  const [
    externalAuditsConductedRemark,
    setExternalAuditsConductedRemark,
  ] = useState("");
  const [documentUrl, setDocumentUrl] = useState("");
  const [errors, setErrors] = useState({});
  
  const [esgRisk, setEsgRisk] = useState("yes");
  const [riskOpportunities, setRiskOpportunities] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await get(`${api}/governance/getGovernance?id=${id}`);

        const record = response.data?.data?.[0] || {};
        const data = record?.transparencyAndDisclosure || {}; // Safely access the transparencyAndDisclosure
        console.log("Fetched Data:", data);

        const rawModule = (
          record?.module ||
          record?._governanceDataRef?.module ||
          record?.entryType ||
          ""
        )
          .toString()
          .toLowerCase()
          .trim();
        if (rawModule.includes("quarter")) setModuleType("quarterly");
        else if (rawModule.includes("annual") || rawModule.includes("month"))
          setModuleType("annual");
        else setModuleType("");

        // Update state with fetched data
        setFinancialReportingFrequency(data.financialReportingFrequency ?? "");
        if (typeof data.hasESGReports?.status === "boolean") {
          setShareholderRights(data.hasESGReports.status ? "yes" : "no");
        } else {
          setShareholderRights("yes");
        }
        const annualDocUrl = data.hasESGReports?.documentUrl || "";
        const quarterlyDocUrl = data.externalAuditsConductedDocumentUrl || "";
        setDocumentUrl(annualDocUrl);
        setExternalAuditsConductedDocumentUrl(quarterlyDocUrl);
        setEsgReportingFrequency(
          data.hasESGReports?.esgReportingFrequency || ""
        );
        if (
          typeof data.reportingStandards?.adheresToReportingStandards ===
          "boolean"
        ) {
          setAdheresToReportingStandards(
            data.reportingStandards.adheresToReportingStandards ? "yes" : "no"
          );
        } else {
          setAdheresToReportingStandards("yes");
        }
        setAdheresToReportingStandardsInfo(
          data.reportingStandards?.adheresToReportingStandardsInfo || ""
        );
        setExternalAuditsConducted(
          data.externalAuditsConducted !== undefined &&
            data.externalAuditsConducted !== null &&
            data.externalAuditsConducted !== ""
            ? formatNumberInputWithCommas(String(data.externalAuditsConducted))
            : "",
        );
        if (typeof data.communicationStakeholders?.esgRisk === "boolean") {
          setEsgRisk(data.communicationStakeholders.esgRisk ? "yes" : "no");
        } else {
          setEsgRisk("yes");
        }
        setRiskOpportunities(
          data.communicationStakeholders?.riskOpportunities || ""
        );
        setExternalAuditsConductedRemark(
          data.externalAuditsConductedRemark || ""
        );
      } catch (error) {
        console.error("Error fetching data:", error.response || error);
      }
    };

    fetchData();
  }, []);

  const validateNumbers = (value) => {
    const raw = stripNumberFormatting(value);
    return /^\d+$/.test(raw) || raw === ""; // Allow only positive integers or empty
  };
  const validateMax365 = (value) => {
    const raw = stripNumberFormatting(value);
    if (!raw) return false;
    const num = Number(raw);
    return !Number.isNaN(num) && num <= 365;
  };

  const validateForm = () => {
    const newErrors = {};

    if (moduleType === "quarterly") {
      if (externalAuditsConducted === "") {
        newErrors.externalAuditsConducted = textError;
      } else if (!validateNumbers(externalAuditsConducted)) {
        newErrors.externalAuditsConducted = numberError;
      }
      if (!esgRisk) {
        newErrors.esgRisk = textError;
      }
      if (showQuarterlyAuditDetails && !externalAuditsConductedDocumentUrl) {
        newErrors.externalAuditsConductedDocumentUrl = "File upload is mandatory.";
      }
    } else {
      if (financialReportingFrequency === "") {
        newErrors.financialReportingFrequency = textError;
      } else if (!validateNumbers(financialReportingFrequency)) {
        newErrors.financialReportingFrequency = numberError;
      } else if (!validateMax365(financialReportingFrequency)) {
        newErrors.financialReportingFrequency = "Value should be less than or equal to 365.";
      }

      if (shareholderRights === "yes" && !documentUrl) {
        newErrors.documentUrl = "File upload is mandatory.";
      }

      if (shareholderRights === "yes") {
        if (!esgReportingFrequency) {
          newErrors.esgReportingFrequency = textError;
        } else if (!validateNumbers(esgReportingFrequency)) {
          newErrors.esgReportingFrequency = numberError;
        } else if (!validateMax365(esgReportingFrequency)) {
          newErrors.esgReportingFrequency = "Value should be less than or equal to 365.";
        }
        if (!adheresToReportingStandards) {
          newErrors.adheresToReportingStandards = textError;
        }
      }

      if (
        shareholderRights === "yes" &&
        adheresToReportingStandards === "yes" &&
        !adheresToReportingStandardsInfo
      ) {
        newErrors.adheresToReportingStandardsInfo = textError;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    const normalizedModuleType =
      moduleType === "quarterly"
        ? "quarterly"
        : moduleType === "annual"
        ? "annual"
        : "annual";

    const annualRequestData = {
      financialReportingFrequency,
      hasESGReports: {
        status: shareholderRights === "yes", // Assuming "yes" means true
        documentUrl,
        esgReportingFrequency,
      },
      reportingStandards: {
        adheresToReportingStandards,
        adheresToReportingStandardsInfo,
      },
    };

    const quarterlyRequestData = {
      externalAuditsConducted: parseInt(
        stripNumberFormatting(externalAuditsConducted),
        10,
      ),
      externalAuditsConductedDocumentUrl,
      externalAuditsConductedRemark,
      communicationStakeholders: {
        esgRisk,
        riskOpportunities,
      },
    };
    const annualPayload = {
      id,
      module: "annual",
      transparencyAndDisclosure: annualRequestData,
      stepsCompleted: {
        transparencyAndDisclosure: true,
      },
    };
    const quarterlyPayload = {
      id,
      module: "quarterly",
      transparencyAndDisclosure: quarterlyRequestData,
      stepsCompleted: {
        transparencyAndDisclosure: true,
      },
    };
    const requestPayload =
      normalizedModuleType === "quarterly" ? quarterlyPayload : annualPayload;

    try {
      const response = await put(`${api}/governance/updateGovernance`, requestPayload);

      console.log("Data saved successfully:", response.data);
      if (normalizedModuleType === "quarterly") {
        navigate("/governance/data");
      } else {
        navigate(`/governance/data/policy-form/${id}`);
      }
    } catch (error) {
      console.error(
        "Error submitting compliance data:",
        error.response?.data || error.message
      );
    }
  };

  const handleTextChange = (e, field, setter, validator) => {
    const value = e.target.value;

    // Set value and validate the field
    setter(value);

    // Validate and clear errors if valid
    if (validator(value)) {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[field]; // Remove error for the specific field
        return newErrors;
      });
    }
  };

  const handleFileChange = (file) => {
    // Set the uploaded file URL
    setDocumentUrl(file);

    // Remove the error once a file is uploaded
    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      delete newErrors.documentUrl; // Remove error for documentUrl
      return newErrors;
    });
  };

  const handleQuarterlyAuditFileChange = (file) => {
    setExternalAuditsConductedDocumentUrl(file);
    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      delete newErrors.externalAuditsConductedDocumentUrl;
      return newErrors;
    });
  };

  const Move = async () => {
    handleSubmit();
    // navigate("/policy_form"); // This is done in handleSubmit
  };

  const Back = async () => {
    navigate(`/governance/data/shareholder-rights/${id}`);
  };

  // Section prefix: Annual should be "D", Quarterly remains "E"
  const sectionPrefix = moduleType === "annual" ? "D" : "E";
  const showQuarterlyAuditDetails =
    Number(stripNumberFormatting(externalAuditsConducted || "0")) > 0;

  return (
    <MainDashboardLayout sidebar={<GovernanceLayout />}>
      <Box
        sx={{
          margin: "20px auto",
          padding: "20px",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#000",
          },
          "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#000",
          },
          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#1976d2",
          },
        }}
      >
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: "bold",
              margin: "1.2% 0",
              textDecoration: "underline",
            }}
          >
            {sectionPrefix}. TRANSPARENCY AND DISCLOSURE
          </Typography>
          {moduleType === "quarterly" ? (
            <>
              <Box sx={{ margin: "2% 0 1% 0", width: "55%" }}>
                <Typography sx={{ fontWeight: "bold", margin: "1% 0" }}>
                  1. Number of External Audits Conducted
                </Typography>
                <TextField
                  fullWidth
                  value={externalAuditsConducted}
                  onChange={(e) => {
                    const formatted = formatNumberInputWithCommas(e.target.value);
                    const raw = stripNumberFormatting(formatted);
                    if (raw !== "" && !/^\d+$/.test(raw)) return;
                    handleTextChange(
                      { target: { value: formatted } },
                      "externalAuditsConducted",
                      setExternalAuditsConducted,
                      validateNumbers
                    );
                    if (Number(raw || "0") <= 0) {
                      setErrors((prev) => {
                        const nextErrors = { ...prev };
                        delete nextErrors.externalAuditsConductedDocumentUrl;
                        delete nextErrors.externalAuditsConductedRemark;
                        return nextErrors;
                      });
                    }
                  }}
                  error={!!errors.externalAuditsConducted}
                  placeholder="Enter Number"
                  variant="outlined"
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                  sx={{
                    "& .MuiInputBase-root": {
                      height: 40,
                    },
                    ml: "1.1rem",
                  }}
                />
                <FormHelperText
                  sx={{ color: "#D34040", fontSize: "14px", marginLeft: "1.1rem" }}
                >
                  {errors.externalAuditsConducted}
                </FormHelperText>
              </Box>
              {showQuarterlyAuditDetails && (
                <Box sx={{ margin: "2% 0 1% 0", textAlign: "left", ml: "1.1rem" }}>
                  <FileUpload
                    setDocumentUrl={handleQuarterlyAuditFileChange}
                    filesList={externalAuditsConductedDocumentUrl}
                    title={"Upload evidence"}
                    isDisabled={true}
                  />
                  {errors.externalAuditsConductedDocumentUrl && (
                    <FormHelperText sx={{ color: "#D34040", fontSize: "14px" }}>
                      {errors.externalAuditsConductedDocumentUrl}
                    </FormHelperText>
                  )}
                </Box>
              )}
              {showQuarterlyAuditDetails && (
                <Box sx={{ margin: "2% 0 1% 0", width: "55%" }}>
                  <TextField
                    fullWidth
                    value={externalAuditsConductedRemark}
                    onChange={(e) =>
                      handleTextChange(
                        e,
                        "externalAuditsConductedRemark",
                        setExternalAuditsConductedRemark,
                        () => true
                      )
                    }
                    placeholder="Enter Additional Text"
                    variant="outlined"
                    error={!!errors.externalAuditsConductedRemark}
                    multiline
                    minRows={1}
                    InputProps={{
                      sx: {
                        padding: "9px 12px",
                        ml: "1.1rem",
                      },
                    }}
                  />
                  <FormHelperText sx={{ color: "#D34040", fontSize: "14px", ml: 2 }}>
                    {errors.externalAuditsConductedRemark}
                  </FormHelperText>
                </Box>
              )}
              <Box sx={{ margin: "2% 0 1% 0" }}>
                <Typography sx={{ fontWeight: "bold", margin: "1% 0" }}>
                  2. Communication on ESG risks/opportunities to stakeholders
                </Typography>
                <RadioGroup
                  row
                  sx={{ ml: "1.1rem" }}
                  value={esgRisk}
                  onChange={(e) => setEsgRisk(e.target.value)}
                >
                  <FormControlLabel
                    value="yes"
                    control={<Radio sx={{ "&.Mui-checked": { color: "#1c486b" } }} />}
                    label="Yes"
                  />
                  <FormControlLabel
                    value="no"
                    control={<Radio sx={{ "&.Mui-checked": { color: "#1c486b" } }} />}
                    label="No"
                  />
                </RadioGroup>
                {errors.esgRisk && (
                  <FormHelperText
                    sx={{ color: "#D34040", fontSize: "14px", marginLeft: "1.1rem" }}
                  >
                    {errors.esgRisk}
                  </FormHelperText>
                )}
              </Box>
              {esgRisk === "yes" && !showQuarterlyAuditDetails && (
                <Box sx={{ margin: "2% 0 1% 0", width: "55%" }}>
                  <TextField
                    fullWidth
                    value={riskOpportunities}
                    onChange={(e) =>
                      handleTextChange(
                        e,
                        "riskOpportunities",
                        setRiskOpportunities,
                        (val) => val.trim() !== ""
                      )
                    }
                    placeholder="Enter text for additional details"
                    variant="outlined"
                    error={!!errors.riskOpportunities}
                    multiline
                    minRows={1}
                    InputProps={{
                      sx: {
                        padding: "9px 12px",
                        ml: "1.1rem",
                      },
                    }}
                  />
                  <FormHelperText sx={{ color: "#D34040", fontSize: "14px", ml: 2 }}>
                    {errors.riskOpportunities}
                  </FormHelperText>
                </Box>
              )}
            </>
          ) : (
            <>

          <Box sx={{ margin: "2% 0 1% 0", width: "55%" }}>
            <Typography sx={{ fontWeight: "bold", margin: "1% 0" }}>
              1. Frequency of Financial Reporting
            </Typography>
            <TextField
              fullWidth
              value={financialReportingFrequency}
              // onChange={(e) => setFinancialReportingFrequency(e.target.value)}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw !== "" && !/^\d+$/.test(raw)) return;
                if (raw !== "" && Number(raw) > 365) return;
                handleTextChange(
                  e,
                  "financialReportingFrequency",
                  setFinancialReportingFrequency,
                  validateNumbers
                );
              }}
              placeholder="Enter Number"
              variant="outlined"
              error={!!errors.financialReportingFrequency}
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
              sx={{
                "& .MuiInputBase-root": {
                  height: 40,
                },
                ml: "1.1rem",
              }}
            />
            <FormHelperText sx={{ color: "#D34040", fontSize: "14px", marginLeft:"1.1rem" }}>
              {errors.financialReportingFrequency}
            </FormHelperText>
          </Box>

          <Box sx={{ margin: "2% 0 1% 0", width: "100%" }}>
            <Typography sx={{ fontWeight: "bold", margin: "1% 0" }}>
              2. Existence of ESG Reports
            </Typography>
            <RadioGroup
              row
              sx={{ ml: "1.1rem" }}
              value={shareholderRights}
              onChange={(e) => {
                const next = e.target.value;
                setShareholderRights(next);
                if (next === "no") {
                  // Clear dependent errors when section is hidden.
                  setErrors((prev) => {
                    const nextErrors = { ...prev };
                    delete nextErrors.documentUrl;
                    delete nextErrors.esgReportingFrequency;
                    delete nextErrors.adheresToReportingStandards;
                    delete nextErrors.adheresToReportingStandardsInfo;
                    return nextErrors;
                  });
                }
              }}
            >
              <FormControlLabel
                value="yes"
                control={<Radio sx={{ "&.Mui-checked": { color: "#1c486b" } }} />}
                label="Yes"
              />
              <FormControlLabel
                value="no"
                control={<Radio sx={{ "&.Mui-checked": { color: "#1c486b" } }} />}
                label="No"
              />
            </RadioGroup>
            <Box sx={{ marginTop: "8px", textAlign: "left", ml: "1.1rem" }}>
              {shareholderRights === "yes" && (
                // <FileUpload setDocumentUrl={handleFileChange} />
                <FileUpload
                  setDocumentUrl={handleFileChange}
                  filesList={documentUrl}
                  title={"Upload evidence"}
                  isDisabled={true}
                />
              )}
              {shareholderRights === "yes" && errors.documentUrl && (
                // <Typography color="error" variant="caption">{errors.documentUrl}</Typography>
                <FormHelperText sx={{ color: "#D34040", fontSize: "14px"}}>
                  {errors.documentUrl}
                </FormHelperText>
              )}
            </Box>
          </Box>

          {shareholderRights === "yes" && (
            <>
              <Box sx={{ width: "55%" }}>
                <Typography sx={{ fontWeight: "bold", margin: "1.5rem 0 0.5rem 0" }}>
                  3. Frequency of ESG Reporting
                </Typography>
                <TextField
                  fullWidth
                  value={esgReportingFrequency}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw !== "" && !/^\d+$/.test(raw)) return;
                    if (raw !== "" && Number(raw) > 365) return;
                    handleTextChange(
                      e,
                      "esgReportingFrequency",
                      setEsgReportingFrequency,
                      validateNumbers
                    );
                  }}
                  placeholder="Enter Number"
                  variant="outlined"
                  error={!!errors.esgReportingFrequency}
                  inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                  sx={{
                    "& .MuiInputBase-root": {
                      height: 40,
                      ml: "1.1rem",
                    },
                  }}
                />
                <FormHelperText sx={{ color: "#D34040", fontSize: "14px", marginLeft: "1.1rem !important" }}>
                  {errors.esgReportingFrequency}
                </FormHelperText>
              </Box>

              <Box sx={{ margin: "2% 0 1% 0" }}>
                <Typography sx={{ fontWeight: "bold", margin: "1% 0" }}>
                  4. Adherence to Reporting Standards (e.g., BRSR, GRI, CDP, SASB)
                </Typography>
                <RadioGroup
                  row
                  sx={{ ml: "1.1rem" }}
                  value={adheresToReportingStandards}
                  onChange={(e) => {
                    const next = e.target.value;
                    setAdheresToReportingStandards(next);
                    if (next === "no") {
                      setErrors((prev) => {
                        const nextErrors = { ...prev };
                        delete nextErrors.adheresToReportingStandardsInfo;
                        return nextErrors;
                      });
                    }
                  }}
                >
                  <FormControlLabel
                    value="yes"
                    control={<Radio sx={{ "&.Mui-checked": { color: "#1c486b" } }} />}
                    label="Yes"
                  />
                  <FormControlLabel
                    value="no"
                    control={<Radio sx={{ "&.Mui-checked": { color: "#1c486b" } }} />}
                    label="No"
                  />
                </RadioGroup>
                <FormHelperText sx={{ color: "#D34040", fontSize: "14px", ml: "1.1rem" }}>
                  {errors.adheresToReportingStandards}
                </FormHelperText>
              </Box>

              {adheresToReportingStandards === "yes" && (
                <Box sx={{ margin: "2% 0 1% 0", width: "55%" }}>
                  <TextField
                    fullWidth
                    value={adheresToReportingStandardsInfo}
                    onChange={(e) =>
                      handleTextChange(
                        e,
                        "adheresToReportingStandardsInfo",
                        setAdheresToReportingStandardsInfo,
                        (val) => val.trim() !== ""
                      )
                    }
                    placeholder="Enter Additional Text"
                    variant="outlined"
                    error={!!errors.adheresToReportingStandardsInfo}
                    multiline
                    minRows={1}
                    InputProps={{
                      sx: {
                        padding: "9px 12px",
                        ml: "1.1rem",
                      },
                    }}
                  />
                  <FormHelperText sx={{ color: "#D34040", fontSize: "14px", ml: 2 }}>
                    {errors.adheresToReportingStandardsInfo}
                  </FormHelperText>
                </Box>
              )}
            </>
          )}
            </>
          )}
        </Box>

        <ActionButtons
          Back={Back}
          Move={Move}
          buttonText={moduleType === "quarterly" ? "Submit" : "Save & Continue"}
        />
      </Box>
    </MainDashboardLayout>
  );
};

export default TransparancyDisclosure;
